import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import Breadcrumb from '../components/Common/Breadcrumb';
import { 
  EMAILJS_SERVICE_ID, 
  EMAILJS_TEMPLATE_ID, 
  EMAILJS_PUBLIC_KEY,
  CONTACT_EMAIL,
  CONTACT_ADDRESS,
  CONTACT_MOBILE
} from '../config';

const Contact = () => {
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const templateParams = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus({ type: 'success', message: 'Thank you! Your message has been sent successfully.' });
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus({ type: 'error', message: 'Sorry, there was an error sending your message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Breadcrumb title="Contact Us" />
      <div className="container-fluid py-5">
        <div className="container py-5">
          <div className="row g-5">
            <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.1s">
              <h1 className="display-5 mb-4">Get In Touch</h1>
              <p className="mb-4">Contact The Trivedi Solutions today for expert guidance on immigration, business setup, or support services in Canada. We're here to help—every step of the way.</p>
              <div className="row g-4">
                <div className="col-12">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 bg-primary rounded-circle p-3">
                      <i className="fa fa-map-marker-alt text-white"></i>
                    </div>
                    <div className="ms-4">
                      <h5>Our Office</h5>
                      <span>{CONTACT_ADDRESS}</span>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 bg-primary rounded-circle p-3">
                      <i className="fa fa-phone text-white"></i>
                    </div>
                    <div className="ms-4">
                      <h5>Call Us</h5>
                      <span>{CONTACT_MOBILE}</span>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 bg-primary rounded-circle p-3">
                      <i className="fa fa-envelope text-white"></i>
                    </div>
                    <div className="ms-4">
                      <h5>Mail Us</h5>
                      <span>{CONTACT_EMAIL}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.3s">
              <form ref={formRef} onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input 
                        type="text" 
                        className={`form-control border-0 bg-light ${errors.name ? 'is-invalid' : ''}`}
                        id="name" 
                        name="name"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="name">Your Name</label>
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input 
                        type="email" 
                        className={`form-control border-0 bg-light ${errors.email ? 'is-invalid' : ''}`}
                        id="email" 
                        name="email"
                        placeholder="Your Email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="email">Your Email</label>
                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-floating">
                      <input 
                        type="text" 
                        className={`form-control border-0 bg-light ${errors.subject ? 'is-invalid' : ''}`}
                        id="subject" 
                        name="subject"
                        placeholder="Subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="subject">Subject</label>
                      {errors.subject && (
                        <div className="invalid-feedback">{errors.subject}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-floating">
                      <textarea 
                        className={`form-control border-0 bg-light ${errors.message ? 'is-invalid' : ''}`}
                        placeholder="Leave a message here" 
                        id="message" 
                        name="message"
                        style={{ height: '150px' }}
                        value={formData.message}
                        onChange={handleInputChange}
                      ></textarea>
                      <label htmlFor="message">Message</label>
                      {errors.message && (
                        <div className="invalid-feedback">{errors.message}</div>
                      )}
                    </div>
                  </div>
                  {submitStatus && (
                    <div className="col-12">
                      <div className={`alert alert-${submitStatus.type === 'success' ? 'success' : 'danger'}`} role="alert">
                        {submitStatus.message}
                      </div>
                    </div>
                  )}
                  <div className="col-12">
                    <button 
                      className="btn btn-primary w-100 py-3" 
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact; 